// assets/eos-hediye.js

/**
 * "Bu bir hediye" + gift note + "Tasarımını paylaş" (blocks/eos-hediye.liquid).
 *
 * Gift: the checkbox itself is `properties[Hediye]` = "Evet", bound to the
 * product form with `form=`, so an unticked box sends nothing. The note lives
 * in a textarea WITHOUT a name; a hidden input carries the cleaned text as
 * `properties[Hediye notu]` and stays disabled (disabled controls are never
 * submitted) while the box is unticked or the note is empty. Horizon builds
 * the add-to-cart body with `new FormData(form)`, which includes every enabled
 * control naming that form, so the sticky bar and the step-by-step flow (both
 * click the real button) carry the gift as well.
 *
 * Share: builds the product URL with the prefill parameters that
 * assets/eos-kisisellestirme.js reads (harf, isim, isim2, burc, burc2) from
 * what the shopper has typed, then tries the Web Share API, then the
 * clipboard, and as a last resort shows the link in a selectable field.
 *
 * No IntersectionObserver / requestAnimationFrame: everything here is driven
 * by input, change and click events.
 */

const EN_FAZLA_VARSAYILAN = 150;
const DURUM_SURESI_MS = 4000;
/** Remaining-character marks at which the counter changes tone and screen readers are told. */
const UYARI_ESIGI = 20;
const DUYURU_NOKTALARI = [20, 10, 0];

/** Line breaks and tabs become a single space: a line item property is one line. */
const SATIR_SONU = /[\r\n\t\v\f]+/g;
/** Control, zero-width and bidi-override characters are never part of a note. */
// eslint-disable-next-line no-control-regex
const GORUNMEZ = /[\u0000-\u0008\u000E-\u001F\u007F-\u009F­​-‏‪-‮⁠-⁤⁦-⁩﻿]/g;

/**
 * @typedef {object} Paylasim
 * @property {string} url Product URL with the prefill parameters.
 * @property {number} alanSayisi How many parameters were filled.
 * @property {string} ozet Human summary for the share text, e.g. "EŞ" or "Koç & Aslan".
 */

/** Personaliser field (data-tur) → prefill parameter, by data-bagli 0 / 1. */
const PARAMETRE = {
  harf: ['harf', 'harf'],
  isim: ['isim', 'isim2'],
  burc: ['burc', 'burc2'],
};

/**
 * Fire-and-forget analytics. A missing or failing analytics API must never
 * break the gift box or the share button, so errors are dropped on purpose.
 * @param {string} ad
 * @param {Record<string, unknown>} veri
 */
function yayinla(ad, veri) {
  try {
    const sonuc = /** @type {any} */ (window).Shopify?.analytics?.publish?.(ad, veri);
    if (sonuc && typeof sonuc.catch === 'function') sonuc.catch(() => {});
  } catch (hata) {
    // Intentionally ignored: analytics is optional.
  }
}

/**
 * Cuts to `uzunluk` UTF-16 units (what `maxlength` counts) without leaving
 * half of an emoji's surrogate pair at the end.
 * @param {string} metin
 * @param {number} uzunluk
 */
function kes(metin, uzunluk) {
  if (metin.length <= uzunluk) return metin;
  const parca = metin.slice(0, uzunluk);
  const son = parca.charCodeAt(parca.length - 1);
  return son >= 0xd800 && son <= 0xdbff ? parca.slice(0, -1) : parca;
}

/**
 * What the shopper sees while typing: invisible characters removed, line
 * breaks turned into spaces, never longer than the limit.
 * @param {string} metin
 * @param {number} enFazla
 */
function yazimTemizle(metin, enFazla) {
  return kes(metin.replace(SATIR_SONU, ' ').replace(GORUNMEZ, ''), enFazla);
}

/**
 * What is sent: as above, plus runs of spaces collapsed and ends trimmed.
 * @param {string} metin
 * @param {number} enFazla
 */
function gonderimTemizle(metin, enFazla) {
  return yazimTemizle(metin, enFazla).replace(/\s{2,}/g, ' ').trim();
}

/**
 * Copies text to the clipboard. Returns whether it worked.
 * @param {string} metin
 * @param {HTMLElement | null} odakGeri Element to give focus back to after the legacy path.
 */
async function kopyala(metin, odakGeri) {
  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(metin);
      return true;
    }
  } catch (hata) {
    // Permission refused or API unavailable: fall through to the legacy path.
  }

  const gecici = document.createElement('textarea');
  try {
    gecici.value = metin;
    gecici.setAttribute('readonly', '');
    gecici.setAttribute('aria-hidden', 'true');
    gecici.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
    document.body.append(gecici);
    gecici.select();
    return document.execCommand('copy');
  } catch (hata) {
    return false;
  } finally {
    gecici.remove();
    odakGeri?.focus({ preventScroll: true });
  }
}

class EosHediye extends HTMLElement {
  /** @type {HTMLInputElement | null} */ #onay = null;
  /** @type {HTMLElement | null} */ #panel = null;
  /** @type {HTMLTextAreaElement | null} */ #not = null;
  /** @type {HTMLInputElement | null} */ #deger = null;
  /** @type {HTMLElement | null} */ #sayac = null;
  /** @type {HTMLElement | null} */ #sayacDuyuru = null;
  /** @type {HTMLButtonElement | null} */ #paylas = null;
  /** @type {HTMLElement | null} */ #durum = null;
  /** @type {HTMLInputElement | null} */ #baglanti = null;
  /** @type {number | undefined} */ #durumZamanlayici;
  /** @type {number | null} */ #sonDuyuru = null;

  connectedCallback() {
    // No product form here (the package builder adds by script): a gift box
    // whose answer never reaches the order would be a false promise.
    if (!(document.getElementById(this.dataset.form ?? '') instanceof HTMLFormElement)) {
      this.hidden = true;
      return;
    }

    this.#onay = this.querySelector('[data-eos-hediye-onay]');
    this.#panel = this.querySelector('[data-eos-hediye-panel]');
    this.#not = this.querySelector('[data-eos-hediye-not]');
    this.#deger = this.querySelector('[data-eos-hediye-deger]');
    this.#sayac = this.querySelector('[data-eos-hediye-sayac]');
    this.#sayacDuyuru = this.querySelector('[data-eos-hediye-sayac-sr]');
    this.#paylas = this.querySelector('[data-eos-hediye-paylas]');
    this.#durum = this.querySelector('[data-eos-hediye-durum]');
    this.#baglanti = this.querySelector('[data-eos-hediye-baglanti]');

    this.#onay?.addEventListener('change', this.#onayDegisti);
    this.#not?.addEventListener('input', this.#notDegisti);
    this.#paylas?.addEventListener('click', this.#paylasTiklandi);

    // The browser may restore the box and the note on back/forward navigation.
    this.#notDegisti();
  }

  disconnectedCallback() {
    this.#onay?.removeEventListener('change', this.#onayDegisti);
    this.#not?.removeEventListener('input', this.#notDegisti);
    this.#paylas?.removeEventListener('click', this.#paylasTiklandi);
    window.clearTimeout(this.#durumZamanlayici);
  }

  get #enFazla() {
    const sayi = Number(this.dataset.enFazla);
    return Number.isFinite(sayi) && sayi > 0 ? sayi : EN_FAZLA_VARSAYILAN;
  }

  /* --- HEDİYE ------------------------------------------------------------ */

  #onayDegisti = () => {
    this.#guncelle();
    if (this.#onay?.checked) {
      yayinla('eos_hediye_isaretlendi', { urun: this.dataset.urun ?? '', tip: this.dataset.tip ?? '' });
    }
  };

  #notDegisti = () => {
    const alan = this.#not;
    if (alan) {
      const once = alan.value;
      const sonra = yazimTemizle(once, this.#enFazla);
      if (sonra !== once) {
        const imlec = alan.selectionStart;
        alan.value = sonra;
        if (imlec !== null && document.activeElement === alan) {
          const konum = Math.max(0, Math.min(sonra.length, imlec - (once.length - sonra.length)));
          alan.setSelectionRange(konum, konum);
        }
      }
    }
    this.#guncelle();
  };

  /** Single place that derives panel visibility, the sent value and the counter. */
  #guncelle() {
    const isaretli = Boolean(this.#onay?.checked);
    if (this.#panel) this.#panel.hidden = !isaretli;

    const temiz = gonderimTemizle(this.#not?.value ?? '', this.#enFazla);
    if (this.#deger) {
      this.#deger.value = temiz;
      this.#deger.disabled = !isaretli || temiz === '';
    }

    this.#sayaciYaz();
  }

  #sayaciYaz() {
    const uzunluk = this.#not?.value.length ?? 0;
    const enFazla = this.#enFazla;
    const kalan = Math.max(0, enFazla - uzunluk);

    if (this.#sayac) {
      this.#sayac.textContent = `${uzunluk}/${enFazla}`;
      this.#sayac.dataset.durum = kalan === 0 ? 'dolu' : kalan <= UYARI_ESIGI ? 'az' : '';
    }

    // Screen readers hear the count only at a few marks, not on every key.
    if (!this.#sayacDuyuru) return;
    const nokta = DUYURU_NOKTALARI.find((n) => kalan === n) ?? null;
    if (nokta === null) {
      if (kalan > UYARI_ESIGI) this.#sonDuyuru = null;
      return;
    }
    if (nokta === this.#sonDuyuru) return;
    this.#sonDuyuru = nokta;
    this.#sayacDuyuru.textContent =
      nokta === 0 ? `Hediye notu ${enFazla} karakter sınırına ulaştı.` : `Hediye notunda ${nokta} karakter hakkın kaldı.`;
  }

  /* --- PAYLAŞ ------------------------------------------------------------ */

  /** @returns {HTMLInputElement[]} The personaliser fields of this product. */
  #kisisellestirmeAlanlari() {
    const kapsam = this.closest('.shopify-section') ?? document;
    const kis =
      kapsam.querySelector('eos-kisisellestirme-component') ?? document.querySelector('eos-kisisellestirme-component');
    return kis ? [...kis.querySelectorAll('input[data-tur]')] : [];
  }

  /** @returns {Paylasim} */
  #baglantiKur() {
    /** @type {URL} */
    let adres;
    try {
      adres = new URL(this.dataset.url || location.pathname, location.origin);
    } catch (hata) {
      adres = new URL(location.pathname, location.origin);
    }

    const parametreler = new URLSearchParams();
    /** @type {string[]} */
    const parcalar = [];
    for (const alan of this.#kisisellestirmeAlanlari()) {
      const radyo = alan.type === 'radio';
      if (radyo && !alan.checked) continue;
      const ad = PARAMETRE[/** @type {keyof typeof PARAMETRE} */ (alan.dataset.tur ?? '')]?.[
        Number(alan.dataset.bagli) ? 1 : 0
      ];
      const deger = radyo ? alan.dataset.kod ?? '' : alan.value.trim();
      if (!ad || !deger || parametreler.has(ad)) continue;
      parametreler.set(ad, deger);
      parcalar.push(radyo ? alan.value : deger);
    }

    const sorgu = parametreler.toString();
    return {
      url: `${adres.origin}${adres.pathname}${sorgu ? `?${sorgu}` : ''}`,
      alanSayisi: [...parametreler.keys()].length,
      ozet: parcalar.join(' & '),
    };
  }

  #paylasTiklandi = async () => {
    const { url, alanSayisi, ozet } = this.#baglantiKur();
    const baslik = this.dataset.baslik || document.title;
    const veri = { title: baslik, text: ozet ? `${baslik}: ${ozet}` : baslik, url };
    this.#durumYaz('', '');
    if (this.#baglanti) this.#baglanti.hidden = true;

    if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(veri))) {
      try {
        await navigator.share(veri);
        this.#paylasildi('paylas', alanSayisi);
        return;
      } catch (hata) {
        // Closing the share sheet is a choice, not a failure: stay quiet.
        if (hata instanceof DOMException && hata.name === 'AbortError') return;
        // Any other refusal (no user activation, blocked by policy) falls back to copying.
      }
    }

    if (await kopyala(url, this.#paylas)) {
      this.#durumYaz(this.dataset.kopyalandi || 'Bağlantı kopyalandı.', 'tamam');
      this.#paylasildi('kopya', alanSayisi);
      return;
    }

    this.#elleGoster(url);
  };

  /**
   * @param {'paylas' | 'kopya'} yontem
   * @param {number} alanSayisi
   */
  #paylasildi(yontem, alanSayisi) {
    yayinla('eos_tasarim_paylasildi', {
      urun: this.dataset.urun ?? '',
      tip: this.dataset.tip ?? '',
      yontem,
      alan_sayisi: alanSayisi,
    });
  }

  /** @param {string} url */
  #elleGoster(url) {
    this.#durumYaz('Bağlantıyı kopyalayamadık; aşağıdaki adresi seçip kopyalayabilirsin.', 'hata');
    const alan = this.#baglanti;
    if (!alan) return;
    alan.value = url;
    alan.hidden = false;
    alan.focus();
    alan.select();
  }

  /**
   * @param {string} metin
   * @param {'' | 'tamam' | 'hata'} tur
   */
  #durumYaz(metin, tur) {
    window.clearTimeout(this.#durumZamanlayici);
    if (!this.#durum) return;
    this.#durum.textContent = metin;
    this.#durum.dataset.tur = tur;
    if (tur === 'tamam') {
      this.#durumZamanlayici = window.setTimeout(() => {
        if (this.#durum) this.#durum.textContent = '';
      }, DURUM_SURESI_MS);
    }
  }
}

if (!customElements.get('eos-hediye-component')) customElements.define('eos-hediye-component', EosHediye);
